import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Paper,
  Grid,
  CircularProgress,
  Autocomplete,
  Card,
  CardContent,
  IconButton,
  Divider,
  Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useAuth } from '../../contexts/AuthContext';
import auth from '../../config/firebase';
import { useNavigate, useParams } from 'react-router-dom';

const CreateDiveTrail = ({ isEdit = false }) => {
  const navigate = useNavigate();
  const { id } = useParams(); // For edit mode
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEdit);
  const [divesites, setDivesites] = useState([]);
  const [selectedDivesites, setSelectedDivesites] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    visibility: 'public',
    visibleTo: [],
    visibleToGroups: [],
    tags: [],
    imageUrl: '',
  });
  const [divers, setDivers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tagInput, setTagInput] = useState('');
  const { userObjectId } = useAuth();

  // Load existing trail data if in edit mode
  useEffect(() => {
    if (isEdit && id) {
      loadTrailData();
    }
  }, [isEdit, id]);

  const loadTrailData = async () => {
    try {
      setInitialLoading(true);
      const user = auth.currentUser;
      const token = user && (await user.getIdToken());
      
      const response = await fetch(`/api/divetrails/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to load trail data');
      }
      
      const trail = await response.json();
      
      // Set form data
      setFormData({
        name: trail.name || '',
        description: trail.description || '',
        visibility: trail.visibility || 'public',
        visibleTo: trail.visibleTo?.map(d => d._id) || [],
        visibleToGroups: trail.visibleToGroups?.map(g => g._id) || [],
        tags: trail.tags || [],
        imageUrl: trail.imageUrl || '',
      });
      
      // Set selected divesites
      if (trail.sites && trail.sites.length > 0) {
        const sitesWithDetails = trail.sites.map(site => ({
          ...site.divesite,
          order: site.order,
          notes: site.notes || '',
        }));
        setSelectedDivesites(sitesWithDetails);
      }
      
    } catch (error) {
      console.error('Error loading trail data:', error);
      setError('Failed to load trail data. Please try again.');
    } finally {
      setInitialLoading(false);
    }
  };

  // Fetch all divesites
  useEffect(() => {
    const fetchDivesites = async () => {
      try {
        const user = auth.currentUser;
        const token = user && (await user.getIdToken());
        
        const response = await fetch('/api/divesites', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch divesites');
        }
        
        const data = await response.json();
        setDivesites(data);
      } catch (error) {
        console.error('Error fetching divesites:', error);
        setError('Failed to load divesites. Please try again later.');
      }
    };
    
    fetchDivesites();
  }, []);

  // Fetch divers and groups for visibility settings
  useEffect(() => {
    const fetchDiversAndGroups = async () => {
      try {
        const user = auth.currentUser;
        const token = user && (await user.getIdToken());
        
        // Fetch divers
        const diversResponse = await fetch('/api/divers', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        if (diversResponse.ok) {
          const diversData = await diversResponse.json();
          setDivers(diversData);
        }
        
        // Fetch groups
        const groupsResponse = await fetch('/api/divergroups', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        if (groupsResponse.ok) {
          const groupsData = await groupsResponse.json();
          setGroups(groupsData);
        }
      } catch (error) {
        console.error('Error fetching divers or groups:', error);
      }
    };
    
    fetchDiversAndGroups();
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Handle adding a tag
  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({
        ...formData,
        tags: [...formData.tags, tagInput.trim()]
      });
      setTagInput('');
    }
  };

  // Handle removing a tag
  const handleRemoveTag = (tagToRemove) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter(tag => tag !== tagToRemove)
    });
  };

  // Handle divesite selection
  const handleDivesiteSelect = (divesite) => {
    if (!selectedDivesites.some(site => site._id === divesite._id)) {
      setSelectedDivesites([
        ...selectedDivesites,
        {
          ...divesite,
          order: selectedDivesites.length + 1,
          notes: ''
        }
      ]);
    }
  };

  // Handle removing a divesite from the trail
  const handleRemoveDivesite = (divesiteId) => {
    const updatedSites = selectedDivesites
      .filter(site => site._id !== divesiteId)
      .map((site, index) => ({
        ...site,
        order: index + 1
      }));
    
    setSelectedDivesites(updatedSites);
  };

  // Handle divesite notes change
  const handleDivesiteNotesChange = (divesiteId, field, value) => {
    const updatedSites = selectedDivesites.map(site => {
      if (site._id === divesiteId) {
        return {
          ...site,
          [field]: value
        };
      }
      return site;
    });
    
    setSelectedDivesites(updatedSites);
  };

  // Handle manual order change
  const handleOrderChange = (divesiteId, newOrder) => {
    const order = parseInt(newOrder);
    if (isNaN(order) || order < 1 || order > selectedDivesites.length) {
      return;
    }

    const updatedSites = [...selectedDivesites];
    const siteIndex = updatedSites.findIndex(site => site._id === divesiteId);
    
    if (siteIndex === -1) return;

    // Remove the site from its current position
    const [movedSite] = updatedSites.splice(siteIndex, 1);
    
    // Insert it at the new position (order - 1 because array is 0-indexed)
    updatedSites.splice(order - 1, 0, movedSite);
    
    // Update all order numbers
    const reorderedSites = updatedSites.map((site, index) => ({
      ...site,
      order: index + 1
    }));
    
    setSelectedDivesites(reorderedSites);
  };

  // Move site up in order
  const moveSiteUp = (divesiteId) => {
    const siteIndex = selectedDivesites.findIndex(site => site._id === divesiteId);
    if (siteIndex > 0) {
      const updatedSites = [...selectedDivesites];
      [updatedSites[siteIndex - 1], updatedSites[siteIndex]] = 
      [updatedSites[siteIndex], updatedSites[siteIndex - 1]];
      
      // Update order numbers
      const reorderedSites = updatedSites.map((site, index) => ({
        ...site,
        order: index + 1
      }));
      
      setSelectedDivesites(reorderedSites);
    }
  };

  // Move site down in order
  const moveSiteDown = (divesiteId) => {
    const siteIndex = selectedDivesites.findIndex(site => site._id === divesiteId);
    if (siteIndex < selectedDivesites.length - 1) {
      const updatedSites = [...selectedDivesites];
      [updatedSites[siteIndex], updatedSites[siteIndex + 1]] = 
      [updatedSites[siteIndex + 1], updatedSites[siteIndex]];
      
      // Update order numbers
      const reorderedSites = updatedSites.map((site, index) => ({
        ...site,
        order: index + 1
      }));
      
      setSelectedDivesites(reorderedSites);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const user = auth.currentUser;
      const token = user && (await user.getIdToken());
      
      if (!token) {
        throw new Error('Authentication required');
      }
      
      // Validate visibility settings
      if (formData.visibility === 'group' && 
          formData.visibleTo.length === 0 && 
          formData.visibleToGroups.length === 0) {
        throw new Error('When visibility is set to group, you must select at least one diver or group');
      }
      
      // Prepare trail data
      const trailData = {
        ...formData,
        sites: selectedDivesites.map(site => ({
          divesite: site._id,
          order: site.order,
          notes: site.notes,
        }))
      };
      
      // Send request to create or update trail
      const url = isEdit 
        ? `/api/divetrails/${id}`
        : '/api/divetrails';
      
      const method = isEdit ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(trailData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to ${isEdit ? 'update' : 'create'} dive trail`);
      }
      
      const data = await response.json();
      setSuccess(`Dive trail ${isEdit ? 'updated' : 'created'} successfully!`);
      
      // Navigate to trail details or trails list after a short delay
      setTimeout(() => {
        navigate(`/divetrails/${data._id}`);
      }, 2000);
      
      // Reset form only if creating new trail
      if (!isEdit) {
        setFormData({
          name: '',
          description: '',
          visibility: 'public',
          visibleTo: [],
          visibleToGroups: [],
          tags: [],
          imageUrl: '',
        });
        setSelectedDivesites([]);
      }
      
    } catch (error) {
      console.error(`Error ${isEdit ? 'updating' : 'creating'} dive trail:`, error);
      setError(error.message || `Failed to ${isEdit ? 'update' : 'create'} dive trail`);
    } finally {
      setLoading(false);
    }
  };

  // Filter divesites based on search query
  const filteredDivesites = divesites.filter(site => {
    const siteName = site.uddf?.divesite?.site?.name?.toLowerCase() || '';
    const siteLocation = site.uddf?.divesite?.site?.geography?.location?.toLowerCase() || '';
    const query = searchQuery.toLowerCase();
    
    return siteName.includes(query) || siteLocation.includes(query);
  });

  // Sort selected divesites by order
  const sortedSelectedDivesites = [...selectedDivesites].sort((a, b) => a.order - b.order);

  if (initialLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Typography variant="h4" gutterBottom>
        {isEdit ? 'Edit Dive Trail' : 'Create New Dive Trail'}
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}
      
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Trail Information
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Trail Name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              multiline
              rows={4}
              required
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Image URL"
              name="imageUrl"
              value={formData.imageUrl}
              onChange={handleInputChange}
              placeholder="https://example.com/image.jpg"
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <TextField
                fullWidth
                label="Add Tags"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddTag();
                  }
                }}
                placeholder="Enter a tag and press Enter or click Add"
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddTag}
                sx={{ ml: 1, minWidth: 100 }}
                disabled={!tagInput.trim()}
              >
                Add Tag
              </Button>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {formData.tags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  onDelete={() => handleRemoveTag(tag)}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Visibility Settings
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Visibility</InputLabel>
              <Select
                name="visibility"
                value={formData.visibility}
                onChange={handleInputChange}
                label="Visibility"
              >
                <MenuItem value="public">Public (visible to everyone)</MenuItem>
                <MenuItem value="private">Private (visible only to you)</MenuItem>
                <MenuItem value="group">Group (visible to selected divers/groups)</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {formData.visibility === 'group' && (
            <>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  multiple
                  options={divers}
                  getOptionLabel={(option) => option.name || option.email || ''}
                  value={divers.filter(d => formData.visibleTo.includes(d._id))}
                  onChange={(e, newValue) => {
                    setFormData({
                      ...formData,
                      visibleTo: newValue.map(v => v._id)
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Visible to Divers"
                      placeholder="Select divers"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  multiple
                  options={groups}
                  getOptionLabel={(option) => option.name || ''}
                  value={groups.filter(g => formData.visibleToGroups.includes(g._id))}
                  onChange={(e, newValue) => {
                    setFormData({
                      ...formData,
                      visibleToGroups: newValue.map(v => v._id)
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Visible to Groups"
                      placeholder="Select groups"
                    />
                  )}
                />
              </Grid>
            </>
          )}
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Select Divesites for Trail
        </Typography>
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="Search Divesites"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name or location"
          />
        </Box>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {filteredDivesites.slice(0, 6).map((site) => (
            <Grid item xs={12} sm={6} md={4} key={site._id}>
              <Card>
                <CardContent>
                  <Typography variant="h6" noWrap>
                    {site.uddf?.divesite?.site?.name || 'Unnamed Site'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {site.uddf?.divesite?.site?.geography?.location || 'No location'}
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleDivesiteSelect(site)}
                    disabled={selectedDivesites.some(s => s._id === site._id)}
                    sx={{ mt: 1 }}
                  >
                    {selectedDivesites.some(s => s._id === site._id) ? 'Added' : 'Add to Trail'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {filteredDivesites.length === 0 && searchQuery && (
          <Alert severity="info" sx={{ mb: 3 }}>
            No divesites found matching "{searchQuery}". Try a different search term.
          </Alert>
        )}

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          Selected Divesites ({selectedDivesites.length})
        </Typography>

        {selectedDivesites.length === 0 ? (
          <Alert severity="warning">
            No divesites selected yet. Search and add divesites to your trail above.
          </Alert>
        ) : (
          <Box>
            {sortedSelectedDivesites.map((site, index) => (
              <Paper
                key={site._id}
                sx={{ 
                  p: 2, 
                  mb: 2, 
                  border: '1px solid #e0e0e0',
                  '&:hover': {
                    boxShadow: 2
                  }
                }}
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={2}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TextField
                        size="small"
                        type="number"
                        value={site.order}
                        onChange={(e) => handleOrderChange(site._id, e.target.value)}
                        inputProps={{ 
                          min: 1, 
                          max: selectedDivesites.length,
                          style: { textAlign: 'center', width: '60px' }
                        }}
                        sx={{ width: '80px' }}
                      />
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <IconButton
                          size="small"
                          onClick={() => moveSiteUp(site._id)}
                          disabled={index === 0}
                          sx={{ p: 0.5 }}
                        >
                          <KeyboardArrowUpIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => moveSiteDown(site._id)}
                          disabled={index === selectedDivesites.length - 1}
                          sx={{ p: 0.5 }}
                        >
                          <KeyboardArrowDownIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle1">
                      {site.uddf?.divesite?.site?.name || 'Unnamed Site'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {site.uddf?.divesite?.site?.geography?.location || 'No location'}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={3}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <IconButton 
                        onClick={() => handleRemoveDivesite(site._id)}
                        color="error"
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Notes for this site"
                      multiline
                      rows={2}
                      value={site.notes}
                      onChange={(e) => handleDivesiteNotesChange(site._id, 'notes', e.target.value)}
                      placeholder="Add any specific notes or instructions for this dive site..."
                    />
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Box>
        )}
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button
          variant="outlined"
          color="secondary"
          size="large"
          onClick={() => navigate('/divetrails')}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={handleSubmit}
          disabled={loading || !formData.name || !formData.description || selectedDivesites.length === 0}
          startIcon={loading && <CircularProgress size={20} color="inherit" />}
        >
          {loading ? (isEdit ? 'Updating...' : 'Creating...') : (isEdit ? 'Update Dive Trail' : 'Create Dive Trail')}
        </Button>
      </Box>
    </Box>
  );
};

export default CreateDiveTrail;